import { z } from "zod";
import { WeeklyHoursSchema } from "../model/WeeklyHours";
import { type TimeOfDay, timeOfDayToDateTime } from "../model/TimeOfDay";
import { type Weekday } from "../model/Weekday";
import {
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { v4 as randomUuid } from "uuid";

type TimetableDto = z.infer<typeof schemas.TimetableDto>;
type CreateTimetableDto = z.infer<typeof schemas.CreateTimetableDto>;
type UpdateTimetableDto = z.infer<typeof schemas.UpdateTimetableDto>;

const _TimetableSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  drivingSchoolId: z.string(),
  lessonsSchedule: WeeklyHoursSchema.transform((val) =>
    val.map(([weekday, from, to]) => {
      const from_ = timeOfDayToDateTime(from);
      const to_ = timeOfDayToDateTime(to);
      const ue = to_.diff(from_, "minutes").minutes / 45;
      return [weekday, from, to, ue] as [Weekday, TimeOfDay, TimeOfDay, number];
    }),
  ),
});
export type Timetable = z.output<typeof _TimetableSchema>;

class TimetablesProvider extends AbstractDataProvider<Timetable> {
  async getOne(_: string, { id }: GetOneParams<Timetable>): Promise<GetOneResult<Timetable>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getTimetable({ params: { id } });
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<Timetable>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.findTimetables({ queries: { drivingSchoolId } });
    const records = dtos.map(_dtoToRecord);
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async create(
    _resource: string,
    params: { data: Pick<Timetable, "drivingSchoolId" | "name" | "lessonsSchedule"> },
  ): Promise<CreateResult<Timetable>> {
    const { data } = params;
    const { lessonsSchedule, ...rest } = data;
    const serverClient = await getAuthenticatedServerClient();
    const createDto: CreateTimetableDto = {
      ...rest,
      idempotencyKey: randomUuid(),
      lessonsSchedule: lessonsSchedule.map(([weekday, from, to, _ue]) => [weekday, from, to]),
    };
    const dto = await serverClient.createTimetable(createDto);
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async update(_resource: string, params: UpdateParams<Timetable>): Promise<UpdateResult<Timetable>> {
    const { id, data } = params;
    const { lessonsSchedule, ...rest } = data;
    const updateDto: UpdateTimetableDto = { ...rest };
    if (lessonsSchedule) {
      updateDto.lessonsSchedule = lessonsSchedule.map(([weekday, from, to, _ue]) => [weekday, from, to]);
    }
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateTimetable(updateDto, { params: { id } });
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async delete(_resource: string, params: DeleteParams): Promise<DeleteResult> {
    const { id } = params;
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteTimetable(undefined, { params: { id } });
    return { data: {} };
  }
}

export const timetablesProvider = new TimetablesProvider();

function _dtoToRecord(dto: TimetableDto): Timetable {
  return _TimetableSchema.parse(dto);
}
